import type { EncounterArr } from '../shared/array-types';
import { encounterCreatureField, encounterField } from '../shared/array-types';
import { reconcilePossessorPermanentStatuses } from '../janus-engine/compile/possessor-compile';
import { handleEncounterRowsPatched } from './encounter-update';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const queues = new Map<string, Promise<void>>();

/** A light/environment frame already saved its raw scalar. Every viewer derives
 * the affected status/defense buckets locally; reload derives them from that same
 * scalar. Only exact missing definitions may be fetched, never a held creature.
 */
export function reconcileEncounterPermanentStatuses(encounter: EncounterArr): Promise<void> {
    const id = String(encounterField(encounter, 'encounter_id') ?? '');
    const run = async (): Promise<void> => {
        const campaignId = encounterField(encounter, 'encounter_master_parent_type') === 'campaign'
            ? String(encounterField(encounter, 'encounter_master_parent_id') ?? '') : '';
        const campaign = window.FateTide?.campaignCache?.get(campaignId) ?? null;
        const hydrated: Record<string, Row | null> = {};
        const changed: string[] = [];
        for (const member of encounter.encounter_creatures_arr ?? []) {
            const row = member as unknown as Row;
            const definition = (key: string): Row | null => hydrated[key] ?? record(record(row.catalog)?.[key])
                ?? record(record(encounter.catalog)?.[key]) ?? record(window.FateTide?.objectCache?.get(key));
            const attempted = new Set<string>();
            for (;;) {
                try {
                    const delta = reconcilePossessorPermanentStatuses(row, { definition,
                        conditionScene: () => ({ encounter: encounter as unknown as Row, campaign }) });
                    if (delta.added.length || delta.removed.length) {
                        const memberId = String(encounterCreatureField(member.encounter_creatures, 'encounter_creature_id') ?? '');
                        changed.push(memberId);
                        const alias = window.FateTide?.casterCache?.get(memberId);
                        if (alias && alias !== member) {
                            const source = record(record(row.computed)?.creature);
                            const target = record(record(alias.computed)?.creature);
                            if (source && target) {
                                const abilities = record(target.abilities) ?? (target.abilities = {}) as Row;
                                const statuses = record(source.abilities)?.status_effect_permanent;
                                if (statuses === undefined) delete abilities.status_effect_permanent; else abilities.status_effect_permanent = statuses;
                                for (const key of ['defenses', 'passive_permanent_status_sources']) {
                                    if (source[key] === undefined) delete target[key]; else target[key] = source[key];
                                }
                            }
                        }
                        window.ftRerenderPcSheet?.(memberId, ['computed.creature.abilities.status_effect_permanent', 'computed.creature.defenses']);
                        window.ftRerenderCreatureStatusList?.(memberId);
                    }
                    break;
                } catch (error) {
                    const missing = /^def-missing:([0-9A-F-]{36})$/i.exec(error instanceof Error ? error.message : String(error))?.[1]?.toUpperCase();
                    if (!missing || attempted.has(missing) || !window.ftDefinitionsForDisplay) throw error;
                    attempted.add(missing);
                    Object.assign(hydrated, await window.ftDefinitionsForDisplay([missing]));
                }
            }
        }
        if (changed.length) handleEncounterRowsPatched(encounter, changed);
    };
    const next = (queues.get(id) ?? Promise.resolve()).catch(() => undefined).then(run);
    queues.set(id, next);
    void next.finally(() => { if (queues.get(id) === next) queues.delete(id); }).catch(() => undefined);
    return next;
}
